// ===========================================
// /admin — MISE CRM (Leah only)
// ===========================================
// Calm, light, legible — warm cream, serif display accents, one terracotta
// accent. Every account that ever signed up: who they are, where they came
// from, what they built, when we last saw them — and the outreach controls
// to act on it without leaving the row.
// Ours-vs-external derived server-side, overridable per account.
// Gated on the admin key; the page is a shell without it.

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

const API = (import.meta as any).env?.VITE_API_URL || 'https://gleaming-elegance-production-d593.up.railway.app/api';

// —— Palette tokens: the ONLY colors on this page ——
const T = {
  bg: '#FAF7F2',
  panel: '#FFFEFB',
  panelHover: '#F2E8DB',
  border: '#E4DED3',
  text: '#37312A',
  text2: '#7D7466',
  faint: '#A79D8C',
  accent: '#B4603C', // terracotta — active filter, links/buttons, bars
  sage: '#6F9B77',   // good / active
  clay: '#BC5F4F',   // churn / bot warnings
} as const;

// Shared class fragments (kept as literals so Tailwind's scanner sees them)
const PANEL = 'rounded-2xl border border-[#E4DED3] bg-[#FFFEFB] shadow-[0_1px_2px_rgba(0,0,0,0.03)]';
const HEAD = 'text-[11px] uppercase tracking-[0.12em] font-medium text-[#A79D8C]';
const KPI_NUM = 'crm-serif text-[26px] font-normal tabular-nums text-[#37312A] leading-relaxed';

type CountVal = number | { count: number; latest?: string | null };
type Account = {
  id: string; name: string; slug: string; type: string; status: string;
  businessType: string | null; ownerEmail: string | null; ownerName: string | null;
  createdAt: string | null; subscriptionStatus: string; trialEndsAt: string | null;
  counts: Record<string, CountVal>; connections: string[]; lastActivityAt: string | null;
  suspectedBot?: boolean; botEvidence?: string[] | null; origin: 'ours' | 'external'; churnPoint?: string | null;
  acquisition?: { referrer?: string; landing?: string; utmSource?: string; utmMedium?: string; utmCampaign?: string; channel?: string } | null;
  usage?: { topAreas: string[]; daysSeen: number; lastDay?: string | null } | null;
  stage: string; crm: { stage?: string; note?: string; lastContactedAt?: string; origin?: string } | null;
  modules?: Record<string, boolean> | null;
};

const STAGES = [
  { key: 'signed_up', label: 'never touched', dot: T.faint },
  { key: 'activated', label: 'built things', dot: T.accent },
  { key: 'connected', label: 'pipe connected', dot: T.sage },
  { key: 'active', label: 'active 7d', dot: T.sage },
] as const;
const STAGE_DOT: Record<string, string> = {
  signed_up: T.faint, activated: T.accent, connected: T.sage, active: T.sage, churned: T.clay,
};
const STAGE_LABEL: Record<string, string> = {
  signed_up: 'never touched', activated: 'built things', connected: 'pipe connected', active: 'active 7d', churned: 'churned',
};
const CRM_STAGES = ['—', 'to contact', 'contacted', 'replied', 'call booked', 'onboarding', 'customer', 'not a fit'];
const COUNT_LABELS: Record<string, string> = {
  wholesaleOrders: 'wholesale orders', productionOrders: 'production orders', menuItems: 'menu items',
  recipes: 'recipes', purchaseOrders: 'purchase orders', invoices: 'invoices', actionItems: 'action items',
};
const countLabel = (k: string) => COUNT_LABELS[k] || k.replace(/([A-Z])/g, ' $1').toLowerCase();
const countOf = (v: CountVal | undefined | null): number => (typeof v === 'number' ? v : v?.count ?? 0);
const builtOf = (a: Account): [string, number][] =>
  Object.entries(a.counts || {}).map(([k, v]) => [k, countOf(v)] as [string, number]).filter(([, n]) => n > 0);

const DAY = 86_400_000;
const parseTs = (iso?: string | null): number | null => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : null;
};
// lastSeen = max of counts[*].latest (when the API sends objects), usage.lastDay, lastActivityAt.
function lastSeenTs(a: Account): number | null {
  let max: number | null = null;
  const push = (iso?: string | null) => { const t = parseTs(iso); if (t !== null && (max === null || t > max)) max = t; };
  push(a.lastActivityAt);
  push(a.usage?.lastDay);
  for (const v of Object.values(a.counts || {})) if (v && typeof v === 'object') push(v.latest);
  return max;
}
function humanAgo(ts: number | null): string {
  if (ts === null) return '—';
  const d = Math.floor((Date.now() - ts) / DAY);
  if (d <= 0) return 'today';
  if (d === 1) return 'yesterday';
  if (d < 14) return `${d}d`;
  if (d < 61) return `${Math.floor(d / 7)}w`;
  if (d < 365) return `${Math.floor(d / 30)}mo`;
  return `${Math.floor(d / 365)}y`;
}
const agoPhrase = (ts: number | null) => {
  const h = humanAgo(ts);
  return h === 'today' || h === 'yesterday' || h === '—' ? h : `${h} ago`;
};

function outreachDraft(a: Account): string {
  const first = (a.ownerName || '').split(' ')[0] || 'there';
  const built = builtOf(a).map(([k, n]) => `${n} ${countLabel(k)}`);
  if (a.stage === 'signed_up') {
    return `Hi ${first},\n\nYou signed up for Mise a while back and I wanted to reach out personally — since then Mise learned to set itself up: drop in your menu (a PDF or even a photo) and it builds your menu, prices and all. Connect your store and sales flow in on their own.\n\nIf running ${a.name} still involves spreadsheets you don't love, I'd genuinely like to show you — or just hear what you were looking for that day.\n\nLeah — founder, Mise`;
  }
  if (built.length) {
    return `Hi ${first},\n\nI noticed you set up ${built[0]}${built.length > 1 ? ` (and more)` : ''} in Mise — and then life happened, as it does.\n\nSince then the part you were missing probably shipped: connect your store or POS and everything flows in on its own — no re-typing. I'd love to show you in 15 minutes, or just hear what stopped you.\n\nLeah — founder, Mise`;
  }
  return `Hi ${first},\n\nChecking in from Mise — I'd love to hear how things are going with ${a.name}, and what would make Mise genuinely useful for you.\n\nLeah`;
}
const draftHref = (a: Account) =>
  `mailto:${a.ownerEmail}?subject=${encodeURIComponent('Mise — from Leah')}&body=${encodeURIComponent(outreachDraft(a))}`;

export default function AdminPage() {
  // Murmur-style type: Instrument Serif for display numbers/title, Work Sans body.
  useEffect(() => {
    if (!document.getElementById('crm-fonts')) {
      const l = document.createElement('link');
      l.id = 'crm-fonts'; l.rel = 'stylesheet';
      l.href = 'https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Work+Sans:wght@400;500&display=swap';
      document.head.appendChild(l);
      const st = document.createElement('style');
      st.id = 'crm-font-classes';
      st.textContent = ".crm-body{font-family:'Work Sans',system-ui,sans-serif}.crm-serif{font-family:'Instrument Serif',Georgia,serif}";
      document.head.appendChild(st);
    }
  }, []);
  const [key, setKey] = useState(() => sessionStorage.getItem('mise_admin_key') || '');
  const [entered, setEntered] = useState(!!key);
  const qc = useQueryClient();
  const [stageFilter, setStageFilter] = useState('');
  const [originFilter, setOriginFilter] = useState<'external' | 'ours' | ''>('external');
  const [q, setQ] = useState('');
  const [showBots, setShowBots] = useState(false);
  const [queueOnly, setQueueOnly] = useState(false);
  const [sortMode, setSortMode] = useState<'seen' | 'new'>('seen');
  const [openId, setOpenId] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-accounts', key],
    queryFn: async () => (await axios.get(`${API}/v1/admin/accounts`, { headers: { 'x-admin-key': key } })).data?.data,
    enabled: entered, retry: false, staleTime: 60_000,
  });
  const saveCrm = useMutation({
    mutationFn: ({ id, crm }: { id: string; crm: any }) =>
      axios.patch(`${API}/v1/admin/accounts/${id}/crm`, crm, { headers: { 'x-admin-key': key } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-accounts', key] }),
  });

  const accounts: Account[] = data?.accounts || [];

  const seenMap = useMemo(() => {
    const m = new Map<string, number | null>();
    for (const a of accounts) m.set(a.id, lastSeenTs(a));
    return m;
  }, [accounts]);
  const seenOf = (a: Account) => seenMap.get(a.id) ?? null;

  const filtered = useMemo(() => accounts.filter((a) =>
    (showBots || !a.suspectedBot) &&
    (!queueOnly || (a.crm?.stage && a.crm.stage !== '—' && a.crm.stage !== 'not a fit')) &&
    (!originFilter || a.origin === originFilter) &&
    (!stageFilter || a.stage === stageFilter) &&
    (!q || [a.name, a.ownerEmail, a.ownerName, a.businessType].some((v) => (v || '').toLowerCase().includes(q.toLowerCase())))
  ), [accounts, stageFilter, q, showBots, originFilter, queueOnly]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const created = (a: Account) => parseTs(a.createdAt) ?? 0;
    if (sortMode === 'new') arr.sort((a, b) => created(b) - created(a));
    else arr.sort((a, b) => {
      const sa = seenMap.get(a.id) ?? null, sb = seenMap.get(b.id) ?? null;
      if (sa !== null && sb !== null) return sb - sa;
      if (sa !== null) return -1;
      if (sb !== null) return 1;
      return created(b) - created(a); // never seen: newest signup first, at the end
    });
    return arr;
  }, [filtered, sortMode, seenMap]);

  // —— THIS WEEK (client-side, non-bot accounts) ——
  const week = useMemo(() => {
    const now = Date.now(), d7 = now - 7 * DAY, d14 = now - 14 * DAY;
    const humans = accounts.filter((a) => !a.suspectedBot);
    const inWindow = (a: Account, from: number, to: number) => {
      const t = parseTs(a.createdAt); return t !== null && t >= from && t < to;
    };
    return {
      newNow: humans.filter((a) => inWindow(a, d7, now + DAY)).length,
      newPrev: humans.filter((a) => inWindow(a, d14, d7)).length,
      active7: humans.filter((a) => { const s = seenMap.get(a.id); return s != null && now - s <= 7 * DAY; }).length,
      connected: humans.filter((a) => (a.connections || []).length > 0).length, // connect dates not in the response — all-time total
      bots: accounts.length - humans.length,
    };
  }, [accounts, seenMap]);

  // —— WHERE THEY COME FROM (non-bot; channel, else referrer hostname, else direct) ——
  const sources = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of accounts) {
      if (a.suspectedBot) continue;
      const acq = a.acquisition;
      let bucket: string;
      if (acq?.channel) bucket = acq.channel;
      else if (acq) {
        const ref = (acq.referrer || '').trim();
        if (!ref) bucket = 'direct';
        else { try { bucket = new URL(ref).hostname.replace(/^www\./, ''); } catch { bucket = ref.replace(/^www\./, ''); } }
      } else bucket = 'before tracking';
      m.set(bucket, (m.get(bucket) || 0) + 1);
    }
    return Array.from(m.entries()).sort((a, b) => b[1] - a[1]).slice(0, 6);
  }, [accounts]);

  // —— FUNNEL (currently filtered set, humans only, each step a subset of the previous) ——
  const funnelSteps = useMemo(() => {
    const now = Date.now();
    const s1 = filtered.filter((a) => !a.suspectedBot);
    const s2 = s1.filter((a) => builtOf(a).length > 0);
    const s3 = s2.filter((a) => (a.connections || []).length > 0);
    const s4 = s3.filter((a) => { const s = seenMap.get(a.id); return s != null && now - s <= 14 * DAY; });
    return [
      { label: 'signed up', n: s1.length },
      { label: 'built something', n: s2.length },
      { label: 'connected', n: s3.length },
      { label: 'active 14d', n: s4.length },
    ];
  }, [filtered, seenMap]);

  // —— WORTH A MESSAGE (external non-bot, scored; zero scores hidden) ——
  const worth = useMemo(() => {
    const now = Date.now();
    return accounts
      .filter((a) => a.origin === 'external' && !a.suspectedBot)
      .map((a) => {
        const seen = seenMap.get(a.id) ?? null;
        const seenD = seen !== null ? (now - seen) / DAY : Infinity;
        const ct = parseTs(a.createdAt);
        const ageD = ct !== null ? (now - ct) / DAY : Infinity;
        const built = builtOf(a);
        const score =
          (seenD <= 7 ? 3 : seenD <= 30 ? 1 : 0) +
          (built.length ? 2 : 0) +
          ((a.connections || []).length ? 1 : 0) +
          (ageD <= 14 ? 1 : 0);
        const reason = [
          `signed up ${agoPhrase(ct)}`,
          built.length ? `built ${built[0][1]} ${countLabel(built[0][0])}${built.length > 1 ? ' & more' : ''}` : 'built nothing yet',
          (a.connections || []).length ? `connected ${a.connections[0]}` : 'never connected',
          ...(seen !== null ? [`seen ${humanAgo(seen)}`] : []),
        ].join(' · ');
        return { a, seen, score, reason };
      })
      .filter((r) => r.score > 0)
      .sort((x, y) => y.score - x.score || (y.seen ?? 0) - (x.seen ?? 0))
      .slice(0, 8);
  }, [accounts, seenMap]);

  if (!entered) return (
    <div className="crm-body min-h-screen flex items-center justify-center bg-[#FAF7F2] p-6">
      <form className={`w-full max-w-sm space-y-4 ${PANEL} p-6`}
        onSubmit={(e) => { e.preventDefault(); const k = key.trim(); setKey(k); sessionStorage.setItem('mise_admin_key', k); setEntered(true); }}>
        <div>
          <h1 className="crm-serif text-[24px] font-normal text-[#37312A]">Mise CRM</h1>
          <p className="text-[13px] text-[#7D7466] leading-relaxed">Admin access only.</p>
        </div>
        <input value={key} onChange={(e) => setKey(e.target.value)} type="password" placeholder="Admin key"
          className="w-full rounded-xl bg-[#FAF7F2] border border-[#E4DED3] px-3 py-2 text-[13px] text-[#37312A] placeholder-[#A79D8C] focus:outline-none focus:border-[#B4603C]" autoFocus />
        <button type="submit" disabled={!key.trim()}
          className="w-full rounded-xl bg-[#B4603C] text-[#FAF7F2] text-[13px] font-medium py-2 hover:opacity-90 disabled:opacity-40">Open</button>
      </form>
    </div>
  );

  if (error) return (
    <div className="crm-body min-h-screen flex items-center justify-center bg-[#FAF7F2]">
      <div className="text-center space-y-3">
        <p className="text-[13px] text-[#7D7466]">That key didn't work.</p>
        <button className="rounded-xl border border-[#E4DED3] px-4 py-2 text-[13px] text-[#7D7466] hover:text-[#37312A] hover:bg-[#F2E8DB]"
          onClick={() => { sessionStorage.removeItem('mise_admin_key'); setEntered(false); setKey(''); }}>Try again</button>
      </div>
    </div>
  );

  const funnel = data?.funnel || {};
  const insights = data?.insights || {};
  const days = (iso?: string | null) => { const t = parseTs(iso); return t !== null ? Math.round((Date.now() - t) / DAY) : null; };
  const fmtDay = (iso?: string | null) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';
  const initials = (a: Account) => (a.name || '??').replace(/[^a-zA-Z ]/g, '').split(' ').filter(Boolean).map((w) => w[0]).slice(0, 2).join('').toUpperCase() || '·';
  const delta = week.newNow - week.newPrev;
  const signupWeeks: any[] = insights.signupsByWeek || [];
  const maxWeek = Math.max(1, ...signupWeeks.map((w: any) => w.n));
  const maxSource = Math.max(1, ...sources.map(([, n]) => n));
  const pipelineCount = accounts.filter((a) => a.crm?.stage && a.crm.stage !== '—' && a.crm.stage !== 'not a fit').length;

  const Dot = ({ color }: { color: string }) => (
    <span className="inline-block w-[6px] h-[6px] rounded-full shrink-0" style={{ backgroundColor: color }} />
  );

  return (
    <div className="crm-body min-h-screen bg-[#FAF7F2] text-[#37312A] text-[13px] leading-relaxed">
      <div className="max-w-[1200px] mx-auto p-5 md:p-6 space-y-6">

        {/* Header — wordmark, count, origin filter, sort toggle */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-baseline gap-4">
            <h1 className="crm-serif text-[28px] font-normal italic leading-none text-[#37312A]">Mise CRM</h1>
            <p className="text-[11px] text-[#A79D8C]">
              {accounts.length} accounts · {funnel.humans ?? '—'} humans · {funnel.payingOrComped ?? '—'} paying or comped
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex rounded-xl border border-[#E4DED3] bg-[#FFFEFB] overflow-hidden">
              {([['external', 'External'], ['ours', 'Ours'], ['', 'All']] as const).map(([v, label]) => (
                <button key={label} onClick={() => setOriginFilter(v)}
                  className={`px-3 py-1.5 text-[13px] ${originFilter === v ? 'bg-[#F2E8DB] text-[#B4603C]' : 'text-[#7D7466] hover:text-[#37312A]'}`}>{label}</button>
              ))}
            </div>
            <div className="flex rounded-xl border border-[#E4DED3] bg-[#FFFEFB] overflow-hidden">
              {([['seen', 'Last seen'], ['new', 'Newest']] as const).map(([v, label]) => (
                <button key={v} onClick={() => setSortMode(v)}
                  className={`px-3 py-1.5 text-[13px] ${sortMode === v ? 'bg-[#F2E8DB] text-[#B4603C]' : 'text-[#7D7466] hover:text-[#37312A]'}`}>{label}</button>
              ))}
            </div>
          </div>
        </div>

        {/* THIS WEEK strip */}
        <div className={`${PANEL} p-5`}>
          <p className={`${HEAD} mb-4`}>This week</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <p className={KPI_NUM}>{week.newNow}</p>
              <p className="text-[11px] text-[#7D7466]">new signups · 7d</p>
              <p className="text-[11px] text-[#A79D8C]">{delta >= 0 ? `+${delta}` : delta} vs prior 7d</p>
            </div>
            <div>
              <p className={KPI_NUM}>{week.active7}</p>
              <p className="text-[11px] text-[#7D7466]">humans active · 7d</p>
            </div>
            <div>
              <p className={KPI_NUM}>{week.connected}</p>
              <p className="text-[11px] text-[#7D7466]">connected</p>
              <p className="text-[11px] text-[#A79D8C]">all-time — connect dates not tracked</p>
            </div>
            <div>
              <p className="crm-serif text-[26px] font-normal tabular-nums text-[#A79D8C] leading-relaxed">{week.bots}</p>
              <p className="text-[11px] text-[#A79D8C]">bots blocked</p>
            </div>
          </div>
        </div>

        {/* Insight panels — 2-col grid */}
        <div className="grid md:grid-cols-2 gap-4">

          {/* Where they come from */}
          <div className={`${PANEL} p-5`}>
            <p className={`${HEAD} mb-4`}>Where they come from</p>
            <div className="space-y-3">
              {sources.map(([label, n]) => (
                <div key={label}>
                  <div className="flex justify-between text-[11px] mb-1">
                    <span className="text-[#7D7466] truncate pr-2">{label}</span>
                    <span className="text-[#37312A] tabular-nums">{n}</span>
                  </div>
                  <div className="h-[3px] rounded-full bg-[#F2E8DB]">
                    <div className="h-full rounded-full" style={{ width: `${(n / maxSource) * 100}%`, backgroundColor: T.accent, opacity: 0.35 + 0.55 * (n / maxSource) }} />
                  </div>
                </div>
              ))}
              {!sources.length && <p className="text-[11px] text-[#A79D8C]">no acquisition data yet — referrers will land here once the campaigns run</p>}
            </div>
          </div>

          {/* Funnel */}
          <div className={`${PANEL} p-5`}>
            <p className={`${HEAD} mb-4`}>Funnel · {originFilter === 'external' ? 'external humans' : originFilter === 'ours' ? 'our accounts' : 'all humans'}{stageFilter ? ' · filtered' : ''}</p>
            <div className="grid grid-cols-4 divide-x divide-[#E4DED3]">
              {funnelSteps.map((s, i) => {
                const prev = i > 0 ? funnelSteps[i - 1].n : null;
                const pct = prev === null ? null : prev > 0 ? Math.round((s.n / prev) * 100) : 0;
                return (
                  <div key={s.label} className={i > 0 ? 'pl-4' : ''}>
                    <p className={KPI_NUM}>{s.n}</p>
                    <p className="text-[11px] text-[#7D7466]">{s.label}</p>
                    <p className="text-[11px] text-[#A79D8C]">{pct === null ? 'in view' : `${pct}% of prev`}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Worth a message */}
          <div className={`${PANEL} p-5`}>
            <p className={`${HEAD} mb-4`}>Worth a message</p>
            {worth.length ? (
              <div className="divide-y divide-[#E4DED3]">
                {worth.map(({ a, reason }) => (
                  <div key={a.id} className="py-2.5 first:pt-0 last:pb-0 flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] text-[#37312A] truncate">{a.name}</p>
                      <p className="text-[11px] text-[#A79D8C] truncate">{reason}</p>
                    </div>
                    {a.ownerEmail && (
                      <a className="shrink-0 rounded-lg border border-[#B4603C]/40 px-2.5 py-1 text-[11px] text-[#B4603C] hover:bg-[#F2E8DB]"
                        href={draftHref(a)}>Draft email</a>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[11px] text-[#A79D8C]">No one worth messaging right now — check back after the next campaign.</p>
            )}
          </div>

          {/* Trends — signups by week, stalls, time spent */}
          <div className={`${PANEL} p-5 space-y-5`}>
            <div>
              <p className={`${HEAD} mb-4`}>Human signups · 12 weeks</p>
              <div className="flex items-end gap-[3px] h-14">
                {signupWeeks.map((w: any) => (
                  <div key={w.week} title={`${w.week}: ${w.n}`} className="flex-1 rounded-sm"
                    style={{ height: `${Math.max(6, (w.n / maxWeek) * 100)}%`, backgroundColor: T.accent, opacity: 0.25 + 0.6 * (w.n / maxWeek) }} />
                ))}
                {!signupWeeks.length && <p className="text-[11px] text-[#A79D8C]">none yet</p>}
              </div>
              {signupWeeks.length > 0 && (
                <div className="flex justify-between text-[11px] text-[#A79D8C] mt-1.5">
                  <span>{signupWeeks[0]?.week}</span><span>{signupWeeks[signupWeeks.length - 1]?.week}</span>
                </div>
              )}
            </div>
            <div>
              <p className={`${HEAD} mb-3`}>Where people stall</p>
              <div className="space-y-1.5">
                {(insights.churnDistribution || []).slice(0, 4).map((c: any) => (
                  <div key={c.label} className="flex justify-between text-[11px]">
                    <span className="text-[#7D7466] truncate pr-2">{c.label}</span>
                    <span className="text-[#37312A] tabular-nums">{c.n}</span>
                  </div>
                ))}
                {!(insights.churnDistribution || []).length && <p className="text-[11px] text-[#A79D8C]">nothing yet</p>}
              </div>
            </div>
            <p className="text-[11px] text-[#A79D8C]">
              {(insights.timeSpentByArea || []).length
                ? `most time in: ${(insights.timeSpentByArea || []).slice(0, 3).map((x: any) => x.area).join(' · ')}`
                : 'time-spent data still collecting'}
              {' '}· tracked since {insights.trackingSince || '2026-09-30'}
            </p>
          </div>
        </div>

        {/* Table toolbar — stage filter, search, pipeline, bots */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex rounded-xl border border-[#E4DED3] bg-[#FFFEFB] overflow-hidden">
            {STAGES.map((s) => (
              <button key={s.key} onClick={() => setStageFilter(stageFilter === s.key ? '' : s.key)}
                className={`px-3 py-1.5 text-[13px] flex items-center gap-1.5 ${stageFilter === s.key ? 'bg-[#F2E8DB] text-[#B4603C]' : 'text-[#7D7466] hover:text-[#37312A]'}`}>
                <Dot color={s.dot} />{s.label}
              </button>
            ))}
          </div>
          <button onClick={() => setQueueOnly(!queueOnly)}
            className={`px-3 py-1.5 text-[13px] rounded-xl border border-[#E4DED3] ${queueOnly ? 'bg-[#F2E8DB] text-[#B4603C]' : 'bg-[#FFFEFB] text-[#7D7466] hover:text-[#37312A]'}`}>
            In pipeline · {pipelineCount}
          </button>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
            className="rounded-xl bg-[#FFFEFB] border border-[#E4DED3] px-3 py-1.5 text-[13px] text-[#37312A] placeholder-[#A79D8C] focus:outline-none focus:border-[#B4603C] w-48" />
          <label className="text-[11px] text-[#A79D8C] flex items-center gap-1.5 ml-auto cursor-pointer">
            <input type="checkbox" checked={showBots} onChange={(e) => setShowBots(e.target.checked)} style={{ accentColor: T.accent }} />
            {funnel.suspectedBots ?? week.bots} bots
          </label>
          <span className="text-[11px] text-[#A79D8C]">{sorted.length} shown</span>
        </div>

        {/* Accounts */}
        {isLoading ? <p className="text-[13px] text-[#A79D8C] p-10 text-center">Counting…</p> : (
          <div className="space-y-2">
            {sorted.map((a) => {
              const open = openId === a.id;
              const seen = seenOf(a);
              const trialDaysLeft = a.trialEndsAt ? Math.ceil((new Date(a.trialEndsAt).getTime() - Date.now()) / DAY) : null;
              const built = builtOf(a);
              const dot = STAGE_DOT[a.stage] || T.faint;
              return (
                <div key={a.id} className={`${PANEL} overflow-hidden`}>
                  <button className={`w-full text-left px-5 py-3 flex items-center gap-4 transition-colors ${open ? 'bg-[#F2E8DB]' : 'hover:bg-[#F2E8DB]'}`}
                    onClick={() => setOpenId(open ? null : a.id)}>
                    <span className="w-8 h-8 rounded-lg border border-[#E4DED3] bg-[#F2E8DB] flex items-center justify-center text-[11px] font-medium text-[#7D7466] shrink-0">{initials(a)}</span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="font-medium text-[13px] text-[#37312A] truncate">{a.name}</span>
                        {a.origin === 'ours' && !a.suspectedBot && <span className="text-[10px] uppercase tracking-wide text-[#A79D8C] border border-[#E4DED3] rounded px-1">ours</span>}
                        {a.suspectedBot && <span className="text-[10px] uppercase tracking-wide text-[#BC5F4F] border border-[#BC5F4F]/40 rounded px-1">bot</span>}
                      </span>
                      <span className="block text-[11px] text-[#A79D8C] truncate">{a.suspectedBot && a.botEvidence ? `${a.ownerEmail || 'no email'} — flagged: ${a.botEvidence.join(' · ')}` : a.crm?.stage === 'to contact' && a.crm?.note ? a.crm.note : `${a.ownerEmail || 'no email'} · ${a.businessType || a.type}`}</span>
                    </span>
                    <span className="text-[13px] text-[#7D7466] tabular-nums w-16 text-right whitespace-nowrap shrink-0">{humanAgo(seen)}</span>
                    <span className="hidden md:flex items-center gap-1.5 w-32 shrink-0">
                      <Dot color={dot} />
                      <span className="text-[11px] text-[#7D7466] truncate">{STAGE_LABEL[a.stage] || a.stage}</span>
                    </span>
                    <span className="hidden lg:block text-[11px] text-[#A79D8C] w-36 truncate">{built.length ? built.map(([k, n]) => `${n} ${countLabel(k).split(' ')[0]}`).slice(0, 2).join(', ') : '—'}</span>
                    <span className="hidden lg:block text-[11px] text-[#A79D8C] w-24 truncate">{(a.connections || []).length ? a.connections.join(', ') : 'no pipes'}</span>
                    <span className="hidden sm:block text-[11px] w-24 text-right whitespace-nowrap">
                      {a.crm?.stage && a.crm.stage !== '—'
                        ? <span className="text-[#7D7466]">{a.crm.stage}</span>
                        : trialDaysLeft !== null && trialDaysLeft <= 3
                          ? <span className="text-[#BC5F4F]">{trialDaysLeft > 0 ? `trial ${trialDaysLeft}d` : 'trial over'}</span>
                          : <span className="text-[#A79D8C]">{a.subscriptionStatus}</span>}
                    </span>
                  </button>

                  {open && (
                    <div className="border-t border-[#E4DED3] px-5 py-5 grid lg:grid-cols-3 gap-6">
                      {/* Journey */}
                      <div className="space-y-3">
                        <p className={HEAD}>Journey</p>
                        <div className="space-y-1.5 text-[13px]">
                          <p className="text-[#7D7466]">Signed up <span className="text-[#A79D8C]">{fmtDay(a.createdAt)} ({agoPhrase(parseTs(a.createdAt))})</span></p>
                          <p className="text-[#7D7466]">Last seen <span className="text-[#A79D8C]">{humanAgo(seen)}</span></p>
                          <p className="text-[#7D7466]">Source: <span className="text-[#A79D8C]">{a.acquisition?.channel || a.acquisition?.utmSource || a.acquisition?.referrer || 'unknown — before tracking'}</span></p>
                          {a.churnPoint
                            ? <p style={{ color: T.clay }}>Stalled: {a.churnPoint}</p>
                            : <p style={{ color: T.sage }}>Active now</p>}
                          <p className="text-[#7D7466]">Billing: <span className="text-[#A79D8C]">{a.subscriptionStatus}{trialDaysLeft !== null ? ` · ${trialDaysLeft > 0 ? `${trialDaysLeft}d of trial left` : 'trial ended'}` : ''}</span></p>
                          {a.suspectedBot && (
                            <p style={{ color: T.clay }}>Flagged as bot: {(a.botEvidence || []).join(' · ')} —{' '}
                              <button onClick={() => saveCrm.mutate({ id: a.id, crm: { bot: 'human' } })}
                                className="underline decoration-dotted underline-offset-2 hover:opacity-80">actually a human</button>
                            </p>
                          )}
                          <p className="text-[#7D7466]">Origin:{' '}
                            <button onClick={() => saveCrm.mutate({ id: a.id, crm: { origin: a.origin === 'ours' ? 'external' : 'ours' } })}
                              className="text-[#B4603C] underline decoration-dotted underline-offset-2 hover:opacity-80">
                              {a.origin} — flip
                            </button>
                          </p>
                        </div>
                      </div>
                      {/* What they built + where time goes */}
                      <div className="space-y-3">
                        <p className={HEAD}>What they built</p>
                        <div className="flex flex-wrap gap-1.5">
                          {built.length ? built.map(([k, n]) => (
                            <span key={k} className="text-[11px] border border-[#E4DED3] text-[#7D7466] rounded px-2 py-0.5">{n} {countLabel(k)}</span>
                          )) : <span className="text-[11px] text-[#A79D8C]">nothing yet</span>}
                          {(a.connections || []).map((c) => (
                            <span key={c} className="text-[11px] border border-[#E4DED3] text-[#7D7466] rounded px-2 py-0.5 inline-flex items-center gap-1.5">
                              <Dot color={T.sage} />{c} connected
                            </span>
                          ))}
                        </div>
                        <p className={`${HEAD} pt-1`}>Where their time goes</p>
                        {a.usage?.topAreas?.length
                          ? <p className="text-[13px] text-[#7D7466]">{a.usage.topAreas.join(' · ')} <span className="text-[#A79D8C]">over {a.usage.daysSeen}d</span></p>
                          : <p className="text-[11px] text-[#A79D8C]">no tracked visits yet</p>}
                      </div>
                      {/* Outreach */}
                      <div className="space-y-3">
                        <p className={HEAD}>Outreach</p>
                        <div className="flex flex-wrap items-center gap-2">
                          <select defaultValue={a.crm?.stage || '—'}
                            className="rounded-lg bg-[#FAF7F2] border border-[#E4DED3] px-2.5 py-1.5 text-[13px] text-[#37312A] focus:outline-none focus:border-[#B4603C]"
                            onChange={(e) => saveCrm.mutate({ id: a.id, crm: { stage: e.target.value } })}>
                            {CRM_STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                          <button className="rounded-lg border border-[#E4DED3] px-2.5 py-1.5 text-[13px] text-[#7D7466] hover:text-[#37312A] hover:bg-[#F2E8DB]"
                            onClick={() => saveCrm.mutate({ id: a.id, crm: { lastContactedAt: new Date().toISOString() } })}>
                            Contacted today
                          </button>
                          {a.ownerEmail && (
                            <a className="rounded-lg border border-[#B4603C]/40 px-2.5 py-1.5 text-[13px] text-[#B4603C] hover:bg-[#F2E8DB]"
                              href={draftHref(a)}>Draft email</a>
                          )}
                        </div>
                        {a.crm?.lastContactedAt && <p className="text-[11px] text-[#A79D8C]">last contacted {days(a.crm.lastContactedAt)}d ago</p>}
                        <textarea defaultValue={a.crm?.note || ''} placeholder="Notes (saved on blur)"
                          className="w-full rounded-lg bg-[#FAF7F2] border border-[#E4DED3] px-3 py-2 text-[13px] text-[#37312A] placeholder-[#A79D8C] min-h-[64px] focus:outline-none focus:border-[#B4603C]"
                          onBlur={(e) => { if (e.target.value !== (a.crm?.note || '')) saveCrm.mutate({ id: a.id, crm: { note: e.target.value } }); }} />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {!sorted.length && <p className="text-[13px] text-[#A79D8C] p-10 text-center">Nothing matches.</p>}
          </div>
        )}
      </div>
    </div>
  );
}
